import type { ComponentType } from "react";
import { useEffect, useRef, useState } from "react";

import dayjs from "dayjs";
import { connect } from "react-redux";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { fonts, setFont, colorsPalettes } from "@similarweb/styles";
import { Box, Title, PlainTooltip } from "@similarweb/ui-components";

// eslint-disable-next-line max-len
import YoutubeKeywordGeneratorChartTooltip from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/youtube-keyword-generator/YoutubeKeywordGeneratorChartTooltip";
import Chart from "pro-features/components/Chart/src/Chart";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import monthlyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/monthlyIntervalConfig";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import { GraphLoader } from "pro-features/components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";
import { WeShare } from "app/components/weshare/weshare";
import { i18nFilter } from "app/filters/i18nFilter";
import { commonOverTimeChartConfigSingle } from "app/pages/website-analysis/incoming-traffic/commonOverTime";
import { DefaultFetchService } from "app/services/fetchService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

const fetchService = DefaultFetchService.getInstance();

const ChartContainer = styled(Box)`
    width: 100%;
    height: 248px;
    margin-bottom: 10px;
    padding: 20px 24px;
    box-sizing: border-box;
`;
const TitleStyled = styled(Title)`
    ${setFont({ $size: 20, $weight: 500, $family: fonts.$dmSansFontFamily })}
    margin-bottom: 20px;
    width: 100%;
    display: flex;
    justify-content: space-between;

    .SWReactIcons {
        margin: 0 10px;
    }
    .Share {
        &:before {
            content: none;
        }
    }
`;
const PlainTooltipWrapper = styled(Title)`
    display: flex;
`;

const CHART_ENDPOINT = "/api/recommendations/YoutubeKeywords/volumeTrend";
const CHART_TYPE = chartTypes.LINE;

const YoutubeKeywordGeneratorChart = ({ keyword, country }) => {
    const [chartData, setChartData] = useState<any>({});
    const [chartConfig, setChartConfig] = useState<any>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const i18n = i18nFilter();
    const chartRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        updateChartConfig();
    }, []);

    useEffect(() => {
        (async () => {
            const shouldFetchVolumeTrend = !!keyword && !!country;
            if (shouldFetchVolumeTrend) {
                try {
                    const response = await fetchService.get(
                        `${CHART_ENDPOINT}?${fetchService.requestParams({
                            keyword,
                            country,
                        })}`,
                    );

                    setChartData({
                        name: keyword,
                        data: Object.entries(response).map(([date, value]) => [Date.parse(date), value]),
                    });
                    setIsLoading(false);
                } catch (ex) {
                    setIsLoading(false);
                }
            }
        })();
    }, [keyword, country]);

    const updateChartConfig = () => {
        const xAxisFormatter = ({ value }) => dayjs.utc(value).utc().format("MMM YY");
        const config = combineConfigs({ type: CHART_TYPE, xAxisFormatter, yAxisFormatter: ({ value }) => value }, [
            monthlyIntervalConfig,
            noLegendConfig,
            yAxisLabelsConfig,
            xAxisLabelsConfig,
            xAxisCrosshair,
            commonOverTimeChartConfigSingle,
            YoutubeKeywordGeneratorChartTooltip,
            {
                colors: [colorsPalettes.blue[400]],
                chart: {
                    height: 160,
                    type: CHART_TYPE,
                    spacingTop: 10,
                    plotBackgroundColor: "transparent",
                    events: {},
                },
                plotOptions: {
                    line: {
                        lineWidth: 2,
                        connectNulls: false,
                    },
                },
                yAxis: {
                    gridLineWidth: 0.5,
                    showFirstLabel: true,
                    showLastLabel: true,
                    reversed: false,
                    gridZIndex: 2,
                    reversedStacks: true,
                    tickPixelInterval: 50,
                    labels: {
                        style: {
                            textTransform: "uppercase",
                            fontSize: "11px",
                            color: "#919191",
                        },
                    },
                },
                xAxis: {
                    gridLineWidth: 0,
                    gridLineDashStyle: "dash",
                    tickLength: 5,
                    labels: {
                        style: {
                            textTransform: "capitalize",
                            fontSize: "11px",
                            color: "#919191",
                        },
                    },
                    minPadding: 0,
                    maxPadding: 0,
                },
            },
        ]);
        setChartConfig(config);
    };

    if (!isLoading && !chartData?.data?.length) {
        return <></>;
    }

    return (
        <ChartContainer ref={chartRef}>
            <TitleStyled>
                <PlainTooltipWrapper>
                    {i18n("youtube.keyword.generator.chart.volume.trend.title", { keyword })}
                    <PlainTooltip
                        placement="top"
                        tooltipContent={i18n("youtube.keyword.generator.chart.tooltip.text")}
                    >
                        <span>
                            <SWReactIcons
                                iconName="info"
                                size="xs"
                            />
                        </span>
                    </PlainTooltip>
                </PlainTooltipWrapper>
                <WeShare
                    chartRef={chartRef.current}
                    title={i18n("youtube.keyword.generator.chart.volume.trend.title", { keyword })}
                    description={i18n("youtube.keyword.generator.chart.tooltip.text")}
                />
            </TitleStyled>
            {isLoading ? (
                <GraphLoader
                    width="100%"
                    height="160px"
                />
            ) : (
                <Chart
                    type={CHART_TYPE}
                    config={chartConfig}
                    data={[chartData]}
                />
            )}
        </ChartContainer>
    );
};

const mapStateToProps = ({ routing: { params } }) => {
    return params;
};

const connected = connect(mapStateToProps)(YoutubeKeywordGeneratorChart) as ComponentType<any>;
export default connected;
